import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipboardList, Plus, Search, Phone, Mail, User as UserIcon, AlertTriangle, FileText, Zap, XCircle, X, DollarSign, CheckCircle } from 'lucide-react';
import { formatDate as formatDateUtil } from '../utils/dateUtils';
import EngagementLetterModal from '../components/cases/EngagementLetterModal';
import QuoteModal from '../components/intake/QuoteModal';
import EngageModal from '../components/intake/EngageModal';
import ReadyToSignModal from '../components/intake/ReadyToSignModal';
import QuickIntakeModal from '../components/intake/QuickIntakeModal';
import FullIntakeModal from '../components/intake/FullIntakeModal';
import IntakeDetailModal from '../components/intake/IntakeDetailModal';
import MarkPaidDialog from '../components/intake/MarkPaidDialog';
import SpaceBackground from '../components/layout/SpaceBackground';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const STATUS_COLORS = {
  new: '#3B82F6',
  screening: '#F59E0B',
  consultation_scheduled: '#8B5CF6',
  consultation_complete: '#10B981',
  qualified: '#059669',
  converted: '#047857',
  declined: '#EF4444',
  rejected: '#DC2626',
  no_response: '#6B7280'
};

function Intake() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [intakes, setIntakes] = useState([]);
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showQuickIntakeModal, setShowQuickIntakeModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quotingIntake, setQuotingIntake] = useState(null);
  const [engagingIntake, setEngagingIntake] = useState(null);
  const [showReadyToSign, setShowReadyToSign] = useState(false);
  const [chainToEngage, setChainToEngage] = useState(false);
  const [stagePill, setStagePill] = useState('');
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [editingIntake, setEditingIntake] = useState(null);

  // Cancel intake state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingIntake, setCancellingIntake] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [markPaidIntake, setMarkPaidIntake] = useState(null);
  const [sendNotification, setSendNotification] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    primary_phone: '',
    email: '',
    source_id: '',
    practice_area: '',
    matter_description: '',
    urgency: 'normal'
  });

  // Pipeline stages (redesign): pills map onto spine states; Stale is server-computed.
  const STAGE_PILLS = [
    { key: '', label: 'All' },
    { key: 'lead,conflict_review,conflict_clear', label: 'Lead' },
    { key: 'consult', label: 'Consult' },
    { key: 'quoted', label: 'Quoted' },
    { key: 'engage_sent,pending_payment', label: 'Sent' },
    { key: 'open', label: 'Opened' },
    { key: 'declined,conflicted', label: 'Declined' },
    { key: '__stale__', label: 'Stale' },
  ];

  useEffect(() => {
    fetchIntakes();
    fetchMetadata();
  }, [currentPage, statusFilter, stagePill]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchIntakes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: '25'
      });
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (stagePill === '__stale__') params.append('stale', 'true');
      else if (stagePill) params.append('pipeline_state', stagePill);

      const response = await axios.get(`${API_URL}/intake?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIntakes(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
      setError(null);
    } catch (err) {
      console.error('Error fetching intakes:', err);
      setError('Failed to load intakes');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [sourcesRes, statusesRes, paRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/intake/sources`, { headers }),
        axios.get(`${API_URL}/intake/statuses`, { headers }),
        axios.get(`${API_URL}/intake/practice-areas`, { headers }),
        axios.get(`${API_URL}/intake/stats/summary?days=30`, { headers })
      ]);

      setSources(sourcesRes.data || []);
      setStatuses(statusesRes.data || []);
      setPracticeAreas(paRes.data || []);
      setStats(statsRes.data || null);
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  };

  const handleViewIntake = (intake) => {
    setSelectedIntake(intake);
    setShowDetailModal(true);
  };

  // A4: run the conflict gate for a lead. clear -> proceeds; potential -> halts.
  const handleConflictGate = async (intake) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/intake/${intake.id}/conflict-check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      if (d.outcome === 'clear') {
        alert('Conflict check CLEAR — this intake may proceed.');
      } else {
        alert(`Conflict check found ${d.potential_conflicts?.length ?? 0} potential conflict(s).\nThe intake is HALTED for attorney review.`);
      }
      fetchIntakes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Conflict check failed');
    }
  };

  // A4: attorney resolution of a conflict_review halt. Notes are required —
  // they are the Bar-defensible record.
  const handleConflictResolve = async (intake) => {
    const choice = window.prompt(
      `Resolve conflict review for ${intake.full_name || intake.id}.\nType CLEAR to clear, or DECLINE to decline for conflict:`);
    if (!choice) return;
    const resolution = choice.trim().toUpperCase() === 'CLEAR' ? 'cleared'
      : choice.trim().toUpperCase() === 'DECLINE' ? 'conflicted' : null;
    if (!resolution) { alert('Type CLEAR or DECLINE.'); return; }
    const notes = window.prompt('Resolution notes (required — recorded on the conflict check):');
    if (!notes || !notes.trim()) { alert('Notes are required.'); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/intake/${intake.id}/conflict-resolution`,
        null,
        { params: { resolution, notes }, headers: { Authorization: `Bearer ${token}` } });
      if (res.data.resolution === 'conflicted') {
        alert('Intake DECLINED for conflict. Decline letter artifact generated'
          + (res.data.notification_sent ? '; prospect notified by email.' : '; EMAIL FAILED — notify the prospect manually.'));
      } else {
        alert('Conflicts cleared — this intake may proceed.');
      }
      fetchIntakes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Resolution failed');
    }
  };

  const handleQuote = (intake) => {
    setQuotingIntake(intake);
    setShowQuoteModal(true);
  };

  const handleGenerateEngagement = (intake) => {
    setSelectedIntake(intake);
    setShowEngagementModal(true);
  };

  const handleEngagementSuccess = (response) => {
    alert(`Engagement letter and invoice created successfully!\n\nEngagement Letter: ${response.engagement_letter_id}\nInvoice: ${response.invoice_number}\nSignature Request: ${response.signature_request_id}`);
    fetchIntakes(); // Refresh list
  };

  const handleCancelIntake = async () => {
    if (!cancellingIntake || !cancelReason.trim()) return;

    try {
      setCancelling(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        reason: cancelReason.trim(),
        send_notification: sendNotification.toString()
      });

      await axios.post(
        `${API_URL}/intake/${cancellingIntake.id}/cancel?${params}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(sendNotification
        ? 'Intake cancelled and notification sent to client.'
        : 'Intake cancelled.');

      setShowCancelModal(false);
      setCancellingIntake(null);
      setCancelReason('');
      setSendNotification(true);
      fetchIntakes();
    } catch (err) {
      console.error('Error cancelling intake:', err);
      alert('Failed to cancel intake: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCancelling(false);
    }
  };

  const openCancelModal = (intake) => {
    setCancellingIntake(intake);
    setCancelReason('');
    setSendNotification(true);
    setShowCancelModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateUtil(dateString);
  };

  const getStatusColor = (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    return status?.color || '#6B7280';
  };

  const getStatusName = (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    return status?.name || 'Unknown';
  };

  if (loading && intakes.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <SpaceBackground />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-virgo-gold-400 flex items-center gap-3">
                <ClipboardList className="w-8 h-8 text-virgo-teal-400" />
                Intake Management
              </h1>
              <p className="text-virgo-teal-300 mt-2">Manage prospective clients and consultation requests</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowReadyToSign(true)}
                className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all flex items-center gap-2"
                title="Consult done, client in hand: create, run the conflict gate, and land at Quote in one flow"
              >
                Ready to Sign
              </button>
              <button
                onClick={() => setShowQuickIntakeModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition-colors"
              >
                <Zap className="w-5 h-5" />
                Quick Intake
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-virgo-navy-600 text-virgo-teal-400 border border-virgo-teal-600 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Full Intake
              </button>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-virgo-navy-700 p-4 rounded-lg shadow">
                <div className="text-sm text-virgo-teal-300">New (30 days)</div>
                <div className="text-2xl font-bold text-virgo-teal-400">{stats.new_intakes || 0}</div>
              </div>
              <div className="bg-virgo-navy-700 p-4 rounded-lg shadow">
                <div className="text-sm text-virgo-teal-300">Qualified</div>
                <div className="text-2xl font-bold text-green-600">{stats.qualified || 0}</div>
              </div>
              <div className="bg-virgo-navy-700 p-4 rounded-lg shadow">
                <div className="text-sm text-virgo-teal-300">Converted</div>
                <div className="text-2xl font-bold text-emerald-600">{stats.converted || 0}</div>
              </div>
              <div className="bg-virgo-navy-700 p-4 rounded-lg shadow">
                <div className="text-sm text-virgo-teal-300">Conversion Rate</div>
                <div className="text-2xl font-bold text-cyan-600">{stats.conversion_rate || 0}%</div>
              </div>
            </div>
          )}
        </div>

        {/* Pipeline stage bar */}
        <div className="flex flex-wrap gap-2 mb-4">
          {STAGE_PILLS.map((p) => (
            <button key={p.key}
              onClick={() => { setStagePill(p.key); setCurrentPage(1); }}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${stagePill === p.key
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-virgo-navy-700 text-virgo-teal-300 hover:bg-virgo-navy-600'}`}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-virgo-navy-700 p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-virgo-teal-500 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchIntakes()}
                  className="w-full pl-10 pr-4 py-2 border border-virgo-teal-700 rounded-lg focus:ring-2 focus:ring-virgo-teal-500 focus:border-blue-500"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-virgo-teal-700 rounded-lg focus:ring-2 focus:ring-virgo-teal-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              {statuses.map(status => (
                <option key={status.id} value={status.id}>{status.name}</option>
              ))}
            </select>
            <button
              onClick={fetchIntakes}
              className="px-6 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition-colors"
            >
              Search
            </button>
          </div>
        </div>

        {/* Intakes Table */}
        <div className="bg-virgo-navy-700 rounded-lg shadow overflow-hidden">
          {error ? (
            <div className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-virgo-teal-300">{error}</p>
            </div>
          ) : intakes.length === 0 ? (
            <div className="p-8 text-center">
              <ClipboardList className="w-12 h-12 text-virgo-teal-500 mx-auto mb-3" />
              <p className="text-virgo-teal-300">No intakes found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-virgo-navy-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Intake ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Practice Area
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Pipeline
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-virgo-navy-700 divide-y divide-gray-200">
                  {intakes.map((intake) => (
                    <tr key={intake.id} className="hover:bg-virgo-navy-600 cursor-pointer" onClick={() => handleViewIntake(intake)}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                        {intake.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <UserIcon className="w-5 h-5 text-virgo-teal-500 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-white">
                              {intake.first_name} {intake.last_name}
                            </div>
                            <div className="text-sm text-virgo-teal-400 flex items-center gap-2 mt-1">
                              {intake.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {intake.email}
                                </span>
                              )}
                              {intake.primary_phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {intake.primary_phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                        {intake.practice_area || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full text-white"
                          style={{ backgroundColor: getStatusColor(intake.status_id) }}
                        >
                          {getStatusName(intake.status_id)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-virgo-navy-600 text-virgo-teal-300">
                          {intake.pipeline_state || 'lead'}
                        </span>
                        {intake.channel && (
                          <span className="ml-1 px-2 py-1 inline-flex text-xs leading-5 rounded-full bg-slate-700 text-slate-300">
                            {intake.channel === 'public_web' ? 'web' : intake.channel}
                          </span>
                        )}
                          {intake.invoice_outstanding && (
                            <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40"
                              title="Case is open (sign-first); the engagement invoice is outstanding">
                              unpaid{intake.invoice_due_date ? ` — due ${new Date(intake.invoice_due_date + 'T00:00:00').toLocaleDateString()}` : ''}
                            </span>
                          )}
                          {intake.consultation_date && (
                            <a href="/calendar" onClick={(e) => e.stopPropagation()}
                              className={`ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${intake.consult_completed_at ? 'bg-green-500/15 text-green-300 border-green-500/40' : 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40'}`}
                              title={intake.booking_appointment_id ? `Booking #${intake.booking_appointment_id} — open calendar` : 'Open calendar'}>
                              {intake.consult_completed_at ? 'consulted ✓' : `consult ${new Date(intake.consultation_date).toLocaleDateString()}`}
                            </a>
                          )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                        {formatDate(intake.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center gap-3 justify-end">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingIntake(intake);
                              setShowCreateModal(true);
                            }}
                            className="text-blue-500 hover:text-blue-400 flex items-center gap-1"
                            title="Edit Full Intake"
                          >
                            <ClipboardList className="w-4 h-4" />
                            <span className="hidden lg:inline">Full Intake</span>
                          </button>
                          {(!intake.pipeline_state || intake.pipeline_state === 'lead') && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleConflictGate(intake); }}
                              className="text-amber-400 hover:text-amber-300 flex items-center gap-1"
                              title="Run the conflict gate (required before consult/quote)"
                            >
                              <span className="hidden lg:inline">Conflict Check</span>
                              <span className="lg:hidden">CC</span>
                            </button>
                          )}
                          {intake.pipeline_state === 'conflict_review' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleConflictResolve(intake); }}
                              className="text-red-400 hover:text-red-300 flex items-center gap-1 font-semibold"
                              title="Potential conflicts found — attorney review required"
                            >
                              <span>Review Conflicts</span>
                            </button>
                          )}
                          {!intake.consult_paid && !intake.consult_fee_waived && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMarkPaidIntake(intake);
                              }}
                              className="text-green-400 hover:text-green-300 flex items-center gap-1"
                              title="Mark Consultation Fee Paid"
                            >
                              <DollarSign className="w-4 h-4" />
                              <span className="hidden lg:inline">Mark Paid</span>
                            </button>
                          )}
                          {(intake.pipeline_state === 'conflict_clear' || intake.pipeline_state === 'quoted') && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleQuote(intake); }}
                              className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                              title="Quote this matter (pre-filled from the fee schedule, fully editable)"
                            >
                              <DollarSign className="w-4 h-4" />
                              <span className="hidden lg:inline">Quote</span>
                            </button>
                          )}
                          {intake.pipeline_state === 'consult' && !intake.consult_completed_at && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const token = localStorage.getItem('token');
                                  await axios.post(`${API_URL}/intake/${intake.id}/consult-complete`, {},
                                    { headers: { Authorization: `Bearer ${token}` } });
                                  fetchIntakes();
                                } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
                              }}
                              className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                              title="Mark the consultation as held (manual staff confirmation)"
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span className="hidden lg:inline">Consulted</span>
                            </button>
                          )}
                          {['quoted', 'engage_sent', 'pending_payment', 'open'].includes(intake.pipeline_state) && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setEngagingIntake(intake); }}
                              className="text-green-500 hover:text-green-400 flex items-center gap-1"
                              title="Engage: letter from quote, attorney approval, one sign+pay send"
                            >
                              <FileText className="w-4 h-4" />
                              <span className="hidden lg:inline">Engage</span>
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openCancelModal(intake);
                            }}
                            className="text-red-500 hover:text-red-400 flex items-center gap-1"
                            title="Cancel Intake"
                          >
                            <XCircle className="w-4 h-4" />
                            <span className="hidden lg:inline">Cancel</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-virgo-teal-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-virgo-navy-600"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-virgo-teal-200">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-virgo-teal-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-virgo-navy-600"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Engagement Letter Modal */}
      {showEngagementModal && (
        <EngagementLetterModal
          isOpen={showEngagementModal}
          onClose={() => {
            setShowEngagementModal(false);
            setSelectedIntake(null);
          }}
          intake={selectedIntake}
          onSuccess={handleEngagementSuccess}
        />
      )}

      {showQuoteModal && (
        <QuoteModal
          isOpen={showQuoteModal}
          onClose={() => { setShowQuoteModal(false); setQuotingIntake(null); }}
          intake={quotingIntake}
          onQuoted={() => {
            fetchIntakes();
            if (chainToEngage && quotingIntake) {
              const it = quotingIntake;
              setShowQuoteModal(false);
              setChainToEngage(false);
              setEngagingIntake({ ...it, pipeline_state: 'quoted' });
            }
          }}
        />
      )}

      {engagingIntake && (
        <EngageModal
          isOpen={!!engagingIntake}
          onClose={() => setEngagingIntake(null)}
          intake={engagingIntake}
          onChanged={() => fetchIntakes()}
        />
      )}

      <ReadyToSignModal
        isOpen={showReadyToSign}
        onClose={() => setShowReadyToSign(false)}
        onConflictClear={(intake) => {
          setShowReadyToSign(false);
          setChainToEngage(true);
          setQuotingIntake(intake);
          setShowQuoteModal(true);
          fetchIntakes();
        }}
      />

      {/* Quick Intake Modal */}
      <QuickIntakeModal
        isOpen={showQuickIntakeModal}
        onClose={() => setShowQuickIntakeModal(false)}
        onSuccess={() => {
          fetchIntakes();
        }}
      />

      {/* Full Intake Modal */}
      <FullIntakeModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingIntake(null);
        }}
        onSuccess={() => {
          fetchIntakes();
          setEditingIntake(null);
        }}
        intake={editingIntake}
      />

      {/* Intake Detail Modal */}
      <IntakeDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedIntake(null);
        }}
        intakeId={selectedIntake?.id}
      />

      {/* Mark Paid Dialog */}
      <MarkPaidDialog
        isOpen={!!markPaidIntake}
        onClose={() => setMarkPaidIntake(null)}
        intake={markPaidIntake}
        onSuccess={() => {
          setMarkPaidIntake(null);
          fetchIntakes();
        }}
      />

      {/* Cancel Intake Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
            <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" onClick={() => setShowCancelModal(false)} />
            <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-virgo-navy-800 border border-virgo-teal-700 shadow-xl rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-virgo-gold-400 flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  Cancel Intake
                </h3>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="text-virgo-teal-400 hover:text-virgo-teal-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {cancellingIntake && (
                <div className="mb-4 p-3 bg-virgo-navy-700 rounded-lg">
                  <p className="text-white font-medium">
                    {cancellingIntake.first_name} {cancellingIntake.last_name}
                  </p>
                  <p className="text-virgo-teal-400 text-sm">
                    {cancellingIntake.email || 'No email'}
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  Reason for Cancellation *
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter reason for cancelling this intake..."
                  rows={3}
                  className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-virgo-teal-500 focus:border-virgo-teal-500"
                />
              </div>

              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendNotification}
                    onChange={(e) => setSendNotification(e.target.checked)}
                    className="w-4 h-4 text-virgo-teal-600 bg-virgo-navy-700 border-virgo-teal-700 rounded focus:ring-virgo-teal-500"
                  />
                  <span className="text-virgo-teal-300 text-sm">
                    Send notification email to client
                  </span>
                </label>
                <p className="mt-1 text-xs text-virgo-teal-500 ml-7">
                  Client will receive an email explaining the cancellation and inviting them to contact you again if needed.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="px-4 py-2 text-virgo-teal-300 hover:text-white transition-colors"
                >
                  Keep Intake
                </button>
                <button
                  onClick={handleCancelIntake}
                  disabled={cancelling || !cancelReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {cancelling ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Cancel Intake
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Intake;
